import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { Button, Screen } from '@dia/ui';
import { useAuth } from '../auth';
import { forgetCachedReads, supabase } from '../supabase';
import { useIdentity } from './lib/identity';
import { Field, HeaderActions, inputClass, Notice, PasswordField } from './lib/ui';

/** How long the sign in button waits for the server before it gives up and says there is no signal. */
const SIGN_IN_PATIENCE_MS = 15_000;

/**
 * The owner console door: Supabase Auth, email and password, in English.
 * Owners can also use a paired phone and a PIN like everyone else. Since
 * migration 018 this door carries the full owner rights on its own.
 */
export function OwnerSignIn() {
  const { t, setLang } = useT();
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const { signOut: endStaffSession } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<'wrong' | 'signal' | null>(null);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotProblem, setForgotProblem] = useState<'no_email' | 'signal' | null>(null);

  // The owner console is English. Set it once when the screen opens.
  useEffect(() => {
    setLang('en');
  }, [setLang]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      // A request that never answers must not leave the button on "One moment"
      // for good. After a while it says there is no signal, and the owner can
      // try again. If the answer turns up late, the screen switches to signed in.
      const outcome = await Promise.race([
        supabase.auth.signInWithPassword({ email: email.trim(), password }),
        new Promise<'timeout'>((resolve) => window.setTimeout(() => resolve('timeout'), SIGN_IN_PATIENCE_MS)),
      ]);
      if (outcome === 'timeout') {
        setProblem('signal');
        return;
      }
      const { error } = outcome;
      if (error) {
        setProblem(/fetch|network/i.test(error.message) ? 'signal' : 'wrong');
        return;
      }
      setPassword('');
      // One person at a time: a crew session left in this browser by an earlier
      // test would otherwise outrank the owner who just signed in, and hide
      // the manager hub and the phones screen from them.
      endStaffSession();
      forgetCachedReads();
      // The home screen: Hoy, with the tab bar and the Mas list under it.
      navigate('/');
    } catch {
      setProblem('signal');
    } finally {
      setBusy(false);
    }
  };

  const onForgot = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setForgotProblem('no_email');
      return;
    }
    setForgotBusy(true);
    setForgotProblem(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: `${window.location.origin}/dueno/restablecer`,
      });
      if (error && !/fetch|network/i.test(error.message)) {
        // Supabase does not say whether the email exists (it never should),
        // so anything other than a network failure still reads as sent.
        setForgotSent(true);
        return;
      }
      if (error) {
        setForgotProblem('signal');
        return;
      }
      setForgotSent(true);
    } catch {
      setForgotProblem('signal');
    } finally {
      setForgotBusy(false);
    }
  };

  if (identity?.kind === 'owner') {
    return (
      <Screen title={t('ops.owner.title')} actions={<HeaderActions badge={false} />}>
        <div className="mt-4 flex flex-col gap-4">
          <Notice tone="ok">{t('ops.owner.signed_in', { email: identity.name })}</Notice>
          <Button block size="lg" onClick={() => navigate('/')}>
            {t('ops.owner.to_console')}
          </Button>
          <Button block variant="secondary" onClick={identity.signOut}>
            {t('action.sign_out')}
          </Button>
        </div>
      </Screen>
    );
  }

  return (
    <Screen title={t('ops.owner.title')} actions={<HeaderActions badge={false} />}>
      <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-5">
        <Field label={t('ops.owner.email')}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            className={inputClass}
          />
        </Field>
        <PasswordField
          label={t('ops.owner.password')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          revealed={revealed}
          onToggle={() => setRevealed((v) => !v)}
        />
        {problem && (
          <Notice tone="danger">{problem === 'wrong' ? t('ops.owner.wrong') : t('ops.owner.no_signal')}</Notice>
        )}
        <Button type="submit" block size="lg" busy={busy} busyLabel={t('loading')}>
          {t('ops.owner.submit')}
        </Button>

        {forgotSent ? (
          <Notice tone="ok">{t('ops.owner.forgot_sent')}</Notice>
        ) : (
          <>
            <Button
              type="button"
              variant="quiet"
              busy={forgotBusy}
              busyLabel={t('loading')}
              onClick={() => void onForgot()}
              className="self-start"
            >
              {t('ops.owner.forgot')}
            </Button>
            {forgotProblem && (
              <Notice tone="danger">
                {forgotProblem === 'no_email' ? t('ops.owner.forgot_needs_email') : t('ops.owner.no_signal')}
              </Notice>
            )}
          </>
        )}

        <Link to="/entrar" className="inline-flex min-h-tap items-center text-accent underline underline-offset-4">
          {t('ops.owner.to_pin')}
        </Link>
      </form>
    </Screen>
  );
}
